import { ElementType, ForwardedRef, forwardRef, isValidElement } from "react"
import { twMerge } from "tailwind-merge"
import { CardHeader } from "./CardHeader"
import { CardContent } from "./CardContent"
import { CardFooter } from "./CardFooter"
import { deepFind, validateDisplayNameChild } from ".."
import { CardContext } from "./CardContext"
import { devWarning } from "../utils/devWarning"
import { SQUARE_CARD_PADDINGS_MAPPING, SQUISH_CARD_PADDINGS_MAPPING } from "./constants"
import type { CardSize, CardPaddingType, CardComponentType, CardProps } from "./types"

function getCardPaddingClasses(paddingType: CardPaddingType, size: CardSize): string {
  if (paddingType === "square") {
    return SQUARE_CARD_PADDINGS_MAPPING[size]
  }

  if (paddingType === "squish") {
    const isNotSquishSize = size === "none" || size === "xxs"

    devWarning(
      isNotSquishSize,
      "'Squish' Card variant does not support these Card sizes: 'none' and 'xxs'.",
    )

    return isNotSquishSize ? "p-0" : SQUISH_CARD_PADDINGS_MAPPING[size]
  }

  return ""
}

const CardComponent: CardComponentType = forwardRef(
  <E extends ElementType = "section">(
    props: Omit<CardProps<E>, "ref">,
    ref?: ForwardedRef<unknown>,
  ) => {
    const { as, className, paddingType, children, ...rest } = props
    const Element = (as || "section") as ElementType
    const size = props.size || "md"

    const _className = twMerge(
      "rounded-lg border border-gray-300 bg-white",
      className,
      size === "lg" ? "space-y-3" : "space-y-2",
      getCardPaddingClasses(paddingType, size),
    )

    const _header = deepFind(children, child => {
      return isValidElement(child) && validateDisplayNameChild(child, "Card.Header")
    })

    const _content = deepFind(children, child => {
      return isValidElement(child) && validateDisplayNameChild(child, "Card.Content")
    })

    const _footer = deepFind(children, child => {
      return isValidElement(child) && validateDisplayNameChild(child, "Card.Footer")
    })

    return (
      <CardContext.Provider value={{ size }}>
        <Element className={_className} {...rest} ref={ref as ForwardedRef<HTMLElement>}>
          {_header}
          {_content}
          {_footer}
        </Element>
      </CardContext.Provider>
    )
  },
)

const Card = Object.assign({}, CardComponent, {
  displayName: "Card",
  Header: CardHeader,
  Content: CardContent,
  Footer: CardFooter,
})

export { Card }
